import api from './api';

/**
 * Service for interacting with member and invitation-related API endpoints
 */
const memberService = {
  /**
   * Get all members of a merchant
   * @param {string} merchantId - UUID of the merchant
   * @returns {Promise} Promise object with members data
   */
  getMerchantMembers: (merchantId) => {
    return api.get(`/merchants/${merchantId}/members/`);
  },

  /**
   * Invite a member to a merchant
   * @param {string} merchantId - UUID of the merchant
   * @param {Object} data - Invitation data
   * @param {string} data.email - Email of the user to invite
   * @param {string} data.role - Role to assign ('manager' or 'staff')
   * @returns {Promise} Promise object with created invitation
   */
  inviteMember: (merchantId, data) => {
    return api.post(`/merchants/${merchantId}/invitations/`, data);
  },

  /**
   * Update a member's role
   * @param {string} userMerchantId - UUID of the user-merchant relationship
   * @param {Object} data - Updated data
   * @param {string} data.role - New role
   * @returns {Promise} Promise object with updated member
   */
  updateMemberRole: (userMerchantId, data) => {
    return api.put(`/merchants/members/${userMerchantId}/`, data);
  },

  /**
   * Remove a member from a merchant
   * @param {string} userMerchantId - UUID of the user-merchant relationship
   * @returns {Promise} Promise object
   */
  removeMember: (userMerchantId) => {
    return api.delete(`/merchants/members/${userMerchantId}/`);
  },

  /**
   * Get all invitations for a merchant
   * @param {string} merchantId - UUID of the merchant
   * @returns {Promise} Promise object with invitations data
   */
  getInvitations: (merchantId) => {
    return api.get(`/merchants/${merchantId}/invitations/`);
  },

  /**
   * Resend an invitation email
   * @param {string} invitationId - UUID of the invitation
   * @returns {Promise} Promise object
   */
  resendInvitation: (invitationId) => {
    return api.post(`/merchants/invitations/${invitationId}/resend/`);
  },

  /**
   * Cancel an invitation
   * @param {string} invitationId - UUID of the invitation
   * @returns {Promise} Promise object
   */
  cancelInvitation: (invitationId) => {
    return api.delete(`/merchants/invitations/${invitationId}/`);
  },

  /**
   * Get invitation details by token (public)
   * @param {string} token - Invitation token
   * @returns {Promise} Promise object with invitation details
   */
  getInvitationByToken: (token) => {
    return api.get(`/merchants/invitations/token/${token}/`);
  },

  /**
   * Accept an invitation
   * @param {string} token - Invitation token
   * @returns {Promise} Promise object with acceptance result
   */
  acceptInvitation: (token) => {
    return api.post(`/merchants/invitations/accept/${token}/`);
  },

  /**
   * Get permissions for a staff member
   * @param {number} userMerchantId - ID of the user-merchant relationship
   * @returns {Promise} Promise object with permissions data
   */
  getMemberPermissions: (userMerchantId) => {
    return api.get(`/merchants/members/${userMerchantId}/permissions/`);
  },

  /**
   * Update permissions for a staff member
   * @param {number} userMerchantId - ID of the user-merchant relationship
   * @param {Object} permissions - Permission key-value pairs
   * @returns {Promise} Promise object with updated permissions
   */
  updateMemberPermissions: (userMerchantId, permissions) => {
    return api.patch(`/merchants/members/${userMerchantId}/permissions/`, { permissions });
  },
};

export default memberService;

